import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IdleConfigService, idleNone } from '@core/auth/idle.config.service';
import { DestroyableComponent } from '@core/components/destroyable/destroyable.component';
import { AlertService } from '@core/services/alert.service';
import { RumService } from '@core/services/rum.service';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { loadMessages, locale } from 'devextreme/localization';
import * as frMessages from 'devextreme/localization/messages/fr.json';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs';
import { AuthService } from './core/auth/auth.service';
import { LoadingService } from './core/services/loading.service';
import { ETicketDownloadNotificationsService } from '@core/services/notifications/eticket-download-notifications.service';
import { NotificationHubService } from '@core/services/notifications/NotificationHub.service';
import { UserInfos } from '@core/models';
import { ReleaseNoteNotificationsService } from '@core/services/notifications/release-note-notifications.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent extends DestroyableComponent implements OnInit, OnDestroy, AfterContentChecked {
  private readonly translate = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly loaderService = inject(LoadingService);
  private readonly idleConfigService = inject(IdleConfigService);
  private readonly alertService = inject(AlertService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly rumService = inject(RumService);

  @ViewChild('scrollableContent', { read: ElementRef }) scrollableContainer: ElementRef;
  @ViewChild('idleMessage') idleMessage: TemplateRef<any>;
  public isAuthenticated: boolean = false;
  loading = this.loaderService.loading;
  isFullscreenRoute = false;
  isTopBlurryEffectVisible = false;
  isBottomBlurryEffectVisible = false;

  public idlecountdown: string;
  isStagingEnvironment: boolean;

  private readonly documentService = inject(ETicketDownloadNotificationsService);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly document: Document = inject(DOCUMENT);
  private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly notificationHubService = inject(NotificationHubService);
  private readonly releaseNoteNotifService = inject(ReleaseNoteNotificationsService);

  constructor() {
    super();

    // i18n
    this.initLocalization();
    if (environment.name === 'Staging') {
      this.isStagingEnvironment = true;
    }
  }

  ngOnInit(): void {
    //scroll to top on router change
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((evt) => {
      if (evt instanceof NavigationEnd) {
        this.isFullscreenRoute = this.isFullscreenRouteActive();
        this.updateWithHeaderClass();
        requestAnimationFrame(() => this.scrollToTop());
        this.rumService.datadogTraceEvent(evt);
        this.cd.markForCheck();
      }
    });

    this.authService.isAuthenticated$
      .pipe(
        takeUntil(this.destroy$),
        tap((isAuthenticated) => {
          this.isAuthenticated = isAuthenticated;
          this.cd.markForCheck();
        }),
        filter(Boolean),
        take(1),
        switchMap(() => this.authService.getUserData().pipe(filter(Boolean), take(1))),
        tap((userInfos) => {
          this.startSignalR(userInfos);
          this.rumService.setUser({
            id: userInfos.sub,
            name: `${userInfos.firstName} ${userInfos.lastName}`,
            email: userInfos.email ?? '',
          });
        }),
        switchMap(() => this.releaseNoteNotifService.loadReleaseNoteNotifications()),
        switchMap(() => this.documentService.loadETicketDownloadNotifications()),
      )
      .subscribe({
        next: (notifications) => {},
        error: (err) => {
          console.error('❌ Erreur dans le flux de notifications', err);
        },
      });

    this.idleConfigService.init(
      () => this.onIdleStart(),
      () => {
        this.alertService.hide();
        this.cd.detectChanges();
      },
      () => this.onIdleTimedout(),
      (sds: number) => this.onIdleWarning(sds),
      idleNone,
    );
  }

  ngAfterContentChecked(): void {
    this.applyBlurryEffect();
  }

  private async startSignalR(userData: UserInfos): Promise<void> {
    if (userData) {
      try {
        await this.notificationHubService.startConnection(userData);
      } catch (err) {
        console.error('Error starting SignalR hub', err);
      }
    }
  }

  scrollToTop() {
    const pageContent = this.document.getElementById('page-content') as HTMLElement | null;
    if (pageContent) {
      pageContent.scrollTop = 0;
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }

  applyBlurryEffect() {
    const el = this.scrollableContainer?.nativeElement;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    this.isTopBlurryEffectVisible = scrollTop > 50;
    this.isBottomBlurryEffectVisible = scrollHeight - scrollTop > 700;
  }

  changeLanguage() {
    this.translate.use(this.translate.currentLang === 'fr' ? 'en' : 'fr');
  }

  onIdleTimedout() {
    this.authService.logoff();
  }

  onIdleWarning(idleSeconds: number) {
    this.idlecountdown =
      idleSeconds > 60
        ? `${Math.floor(idleSeconds / 60)} minute(s) et ${idleSeconds % 60} seconde(s)`
        : `${idleSeconds} seconde(s)`;
    this.cd.markForCheck();
  }

  onIdleStart() {
    this.alertService.showTemplate(this.idleMessage, 'warning', environment.IdleKeepAlive.Timeout * 1000);
  }

  initLocalization() {
    this.translate.addLangs(['en', 'fr']);
    this.translate.setDefaultLang('fr');
    this.translate.use('fr');
    loadMessages(frMessages);
    locale('fr');
  }

  private updateWithHeaderClass() {
    const pageContent = this.document.getElementById('page-content');
    if (!pageContent) return;

    const showHeader = this.getHeaderVisibility(this.activatedRoute);
    this.setHeaderClass(pageContent, showHeader);
  }

  private isFullscreenRouteActive(): boolean {
    let route: ActivatedRoute | null = this.activatedRoute;
    while (route) {
      if (route.snapshot?.data?.['fullscreen'] === true) return true;
      route = route.firstChild;
    }
    return false;
  }

  private getHeaderVisibility(route: ActivatedRoute | null): boolean {
    let hasHeader: boolean | undefined;

    while (route) {
      const headerFlag = route.snapshot?.data?.['hasHeader'];
      if (headerFlag !== undefined) {
        hasHeader = headerFlag;
      }
      route = route.firstChild!;
    }

    return hasHeader ?? true;
  }

  private setHeaderClass(element: HTMLElement, showHeader: boolean) {
    if (showHeader) {
      this.addHeaderClass(element);
    } else {
      this.removeHeaderClass(element);
    }
  }

  private addHeaderClass(element: HTMLElement) {
    this.renderer.addClass(element, 'with-header');
  }

  private removeHeaderClass(element: HTMLElement) {
    this.renderer.removeClass(element, 'with-header');
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.notificationHubService.stopConnection();
  }
}
